import * as React from 'react'
import * as HoverCard from '@radix-ui/react-hover-card'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { tw } from 'utils/classnames'

const DEFAULT_DELAY = 300
const DEFAULT_OFFSET = 4
const ARROW_WIDTH = 12
const ARROW_HEIGHT = 6
const MAX_WIDTH_PX = 200
const DEFAULT_RICH_OPEN_DELAY = 200
// HoverCard with closeDelay 0 dismisses the instant the pointer leaves the
// trigger, so the cursor cannot cross the arrow/offset gap to reach buttons
// or previews inside the card (lightbox chips). 300ms is Radix's own default.
const RICH_TOOLTIP_CLOSE_DELAY = 300

// Invisible ::after that fills the trigger↔content gap so the pointer never
// "leaves" while traveling toward the card. data-side (not the preferred
// `side` prop) is what Radix sets after collision flipping.
const SIDE_HITBOX = {
  top: 'data-[side=top]:after:absolute data-[side=top]:after:-bottom-[--hitbox-size] data-[side=top]:after:left-0 data-[side=top]:after:h-[--hitbox-size] data-[side=top]:after:w-full data-[side=top]:after:content-[""]',
  bottom:
    'data-[side=bottom]:after:absolute data-[side=bottom]:after:-top-[--hitbox-size] data-[side=bottom]:after:left-0 data-[side=bottom]:after:h-[--hitbox-size] data-[side=bottom]:after:w-full data-[side=bottom]:after:content-[""]',
  right:
    'data-[side=right]:after:absolute data-[side=right]:after:-left-[--hitbox-size] data-[side=right]:after:top-0 data-[side=right]:after:h-full data-[side=right]:after:w-[--hitbox-size] data-[side=right]:after:content-[""]',
  left: 'data-[side=left]:after:absolute data-[side=left]:after:-right-[--hitbox-size] data-[side=left]:after:top-0 data-[side=left]:after:h-full data-[side=left]:after:w-[--hitbox-size] data-[side=left]:after:content-[""]',
} as const

interface ButtonTooltipProps {
  children: React.ReactNode
  value: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  offset?: number
  className?: string
  disabled?: boolean
  delay?: number
  /** When true, allows the label to wrap across multiple lines (max 200px). */
  multiline?: boolean
  /** Optional keyboard shortcut badge (e.g. ⌘K). */
  shortcut?: React.ReactNode
  /** Optional media preview shown above the label. */
  media?: React.ReactNode
  /**
   * Close the tooltip when the pointer enters the label itself. Use when a
   * side-placed tooltip would sit over a neighboring control (e.g. sidebar
   * icons) and keep capturing hover on the way to that control.
   */
  dismissOnContentHover?: boolean
}

/**
 * Plain tooltip — brief contextual label for a control on hover or focus.
 *
 * @figma https://www.figma.com/design/mKWuIdPpAnPyyQnGc1pbY5/Studio-DS---Components?node-id=590-703
 */
export function ButtonTooltip({
  children,
  value,
  side = 'top',
  className,
  offset = DEFAULT_OFFSET,
  disabled,
  delay = DEFAULT_DELAY,
  multiline = false,
  shortcut,
  media,
  dismissOnContentHover = false,
}: ButtonTooltipProps) {
  const [open, setOpen] = React.useState(false)

  function resolveOpen() {
    if (disabled) {
      return false
    }
    if (dismissOnContentHover) {
      return open
    }
    return undefined
  }

  return (
    <TooltipPrimitive.Provider delayDuration={delay}>
      <TooltipPrimitive.Root
        open={resolveOpen()}
        onOpenChange={dismissOnContentHover ? setOpen : undefined}
      >
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={offset}
            onPointerEnter={dismissOnContentHover ? () => setOpen(false) : undefined}
            className={tw(
              'z-50 flex max-w-[200px] flex-col items-center',
              'gap-[var(--studio-geometry-component-control-tooltip-body-gap)]',
              'rounded-studio-geometry-semantic-radius-sm',
              'bg-theme_component_control_tooltip_default-background-color',
              'px-[var(--studio-geometry-component-control-tooltip-body-padding-x)]',
              'py-[var(--studio-geometry-component-control-tooltip-body-padding-y)]',
              'shadow-studio-shadow-l2',
              className,
            )}
            style={{ maxWidth: MAX_WIDTH_PX }}
          >
            {media ? (
              <div className="min-w-[120px] w-full overflow-hidden rounded-studio-geometry-semantic-radius-xs border border-theme_semantic_surface_border_inverse-color-faint">
                {media}
              </div>
            ) : null}
            <div className="flex max-w-full flex-wrap items-center justify-center gap-[var(--studio-geometry-component-control-tooltip-label-gap)] px-[var(--studio-geometry-component-control-tooltip-label-gap)]">
              <span
                className={tw(
                  'studio-typography-12 text-theme_semantic_surface_ui_foreground-color-inverse-strong text-center',
                  multiline ? 'whitespace-normal break-words' : 'truncate',
                )}
              >
                {value}
              </span>
              {shortcut ? <ShortcutBadge>{shortcut}</ShortcutBadge> : null}
            </div>
            <TooltipPrimitive.Arrow asChild width={ARROW_WIDTH} height={ARROW_HEIGHT}>
              <PlainTooltipArrow />
            </TooltipPrimitive.Arrow>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}

function ShortcutBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="studio-typography-10 shrink-0 rounded-studio-geometry-semantic-radius-sm bg-theme_component_control_kbd_background-color-inverse px-1 py-px text-theme_semantic_surface_ui_foreground-color-inverse-faint">
      {children}
    </span>
  )
}

function PlainTooltipArrow() {
  return (
    <svg
      width={ARROW_WIDTH}
      height={ARROW_HEIGHT}
      viewBox={`0 0 ${ARROW_WIDTH} ${ARROW_HEIGHT}`}
      preserveAspectRatio="none"
      style={{ fill: 'var(--theme_component_control_tooltip_default-background-color)' }}
      aria-hidden
    >
      <path d={`M0 0 L${ARROW_WIDTH / 2} ${ARROW_HEIGHT} L${ARROW_WIDTH} 0`} />
    </svg>
  )
}

/**
 * How the card is revealed. Radix's HoverCard ignores touch input entirely, so
 * `tap` and `both` add the touch handling it leaves out. Keep the default
 * (`hover`) when the trigger is itself interactive — a link or button — since a
 * tap there already belongs to the trigger's own action.
 */
export type RichTooltipMode = 'hover' | 'tap' | 'both'

interface RichTooltipContextValue {
  mode: RichTooltipMode
  toggleOpen: () => void
}

const RichTooltipContext = React.createContext<RichTooltipContextValue | null>(null)

function RichTooltipRoot({
  mode = 'hover',
  openDelay = DEFAULT_RICH_OPEN_DELAY,
  closeDelay = RICH_TOOLTIP_CLOSE_DELAY,
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof HoverCard.Root> & { mode?: RichTooltipMode }) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false)
  const isControlled = openProp !== undefined
  const open = isControlled ? openProp : uncontrolledOpen

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(next)
      }
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange],
  )

  const context = React.useMemo<RichTooltipContextValue>(
    () => ({ mode, toggleOpen: () => setOpen(!open) }),
    [mode, open, setOpen],
  )

  return (
    <RichTooltipContext value={context}>
      <HoverCard.Root
        openDelay={openDelay}
        closeDelay={closeDelay}
        open={open}
        onOpenChange={next => {
          // Opens routed through Radix come from hover or focus; `tap` mode
          // drops them so only an explicit tap reveals the card. Closes always
          // pass through, including the outside-press dismissal a tap relies on.
          if (next && mode === 'tap') {
            return
          }
          setOpen(next)
        }}
        {...props}
      />
    </RichTooltipContext>
  )
}

function RichTooltipTrigger({
  children,
  asChild = true,
}: {
  children: React.ReactNode
  asChild?: boolean
}) {
  const context = React.use(RichTooltipContext)
  const respondsToTap = context?.mode === 'tap' || context?.mode === 'both'

  return (
    <HoverCard.Trigger
      asChild={asChild}
      // HoverCard preventDefaults touchstart, which suppresses the simulated
      // click, so a tap has to be picked up on pointerdown instead.
      onPointerDown={
        respondsToTap
          ? event => {
              if (event.pointerType === 'mouse') {
                return
              }
              context?.toggleOpen()
            }
          : undefined
      }
    >
      {children}
    </HoverCard.Trigger>
  )
}

function RichTooltipContent({
  side = 'top',
  sideOffset = DEFAULT_OFFSET,
  forceMount,
  className,
  children,
  style,
  ...props
}: React.ComponentPropsWithoutRef<typeof HoverCard.Content>) {
  return (
    <HoverCard.Portal forceMount={forceMount}>
      <HoverCard.Content
        forceMount={forceMount}
        side={side}
        sideOffset={sideOffset}
        style={
          {
            ...style,
            '--hitbox-size': `${sideOffset + 1}px`,
          } as React.CSSProperties
        }
        className={tw(
          'relative z-50 rounded-studio-geometry-semantic-radius-md bg-theme_semantic_surface_pop-over_primary-background border border-theme_semantic_surface_border_color-regular p-1 shadow-studio-shadow-l2 after:z-10',
          SIDE_HITBOX.top,
          SIDE_HITBOX.bottom,
          SIDE_HITBOX.right,
          SIDE_HITBOX.left,
          forceMount && 'data-[state=closed]:invisible',
          className,
        )}
        {...props}
      >
        {children}
        <RichTooltipArrow />
      </HoverCard.Content>
    </HoverCard.Portal>
  )
}

const RICH_ARROW_WIDTH = 10
const RICH_ARROW_HEIGHT = 5

function RichTooltipArrow() {
  return (
    <HoverCard.Arrow asChild width={RICH_ARROW_WIDTH} height={RICH_ARROW_HEIGHT}>
      {/*
        Radix's default arrow is a bare filled polygon, so the card's border stops
        at the arrow's base instead of following its edges. Stroking only the two
        diagonals continues the border, and pulling the arrow 1px into the card
        lets its fill hide the border segment that would otherwise cross the base.
        The colours are the dark-theme resolutions of the card's own
        pop-over_primary-background and surface_border_color-regular tokens.
      */}
      <svg
        viewBox={`0 0 ${RICH_ARROW_WIDTH} ${RICH_ARROW_HEIGHT}`}
        preserveAspectRatio="none"
        className="overflow-visible"
        style={{ transform: 'translateY(-1px)' }}
      >
        <path
          d={`M0 0 L${RICH_ARROW_WIDTH / 2} ${RICH_ARROW_HEIGHT} L${RICH_ARROW_WIDTH} 0`}
          className="fill-studio-gray-1000 stroke-studio-transparent-gray-0-20"
          strokeWidth={1}
          strokeLinejoin="round"
        />
      </svg>
    </HoverCard.Arrow>
  )
}

export const RichTooltip = Object.assign(RichTooltipRoot, {
  Trigger: RichTooltipTrigger,
  Content: RichTooltipContent,
})
